import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined"
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined"
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import CardHeader from "@mui/material/CardHeader"
import Divider from "@mui/material/Divider"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"
import { useEffect, useState } from "react"

import {
  MULTIPART_HEADERS,
  useAuthorizedAxiosClient,
} from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import EmailSignatureEditor, {
  useEmailSignatureEditorState,
} from "../components/EmailSignatureEditor"
import SignatureImageUpload from "../components/SignatureImageUpload"
import {
  CONCIERGE_EMAIL_SIGNATURE_API_URL,
  buildInitialEditorHtml,
  savedSignatureHasContent,
  wrapSignaturePreview,
} from "../components/emailSignatureUtils"
import { recruitProfileSectionCardSx } from "./recruitProfileSectionStyles"

function SignaturePreviewPanel({ previewHtml, hasSavedSignature }) {
  const theme = useTheme()
  const accent = theme.palette.colors?.brand500 ?? theme.palette.primary.main

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Row sx={{ alignItems: "center", gap: 1 }}>
          <MailOutlineOutlinedIcon sx={{ color: accent, fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Live preview
          </Typography>
        </Row>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          Appended automatically when you send inbox email replies for this
          account
        </Typography>
      </Box>
      <Box sx={{ flex: 1, p: 2, minHeight: { xs: 180, md: 240 } }}>
        {previewHtml ? (
          <Typography
            component="div"
            variant="body2"
            sx={{
              "& img": { maxWidth: "100%", height: "auto" },
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        ) : (
          <Column
            sx={{ alignItems: "center", py: 3, gap: 1, textAlign: "center" }}
          >
            <DrawOutlinedIcon sx={{ fontSize: 36, color: "text.disabled" }} />
            <Typography variant="body2" color="text.secondary">
              {hasSavedSignature
                ? "Start editing to see your signature preview"
                : "Create your signature to see a live preview here"}
            </Typography>
          </Column>
        )}
      </Box>
    </Box>
  )
}

export default function ConciergeEmailSignatureSection({ onStatusMessage }) {
  const theme = useTheme()
  const axios = useAuthorizedAxiosClient()
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [previewHtml, setPreviewHtml] = useState("")
  const [hasSavedSignature, setHasSavedSignature] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [loadedHtml, setLoadedHtml] = useState(null)
  const {
    editorReady,
    setEditorReady,
    editorRef,
    updateEditorContents,
    getEditorHtml,
    insertImageIntoEditor,
    signatureHtmlHasContent: editorHasContent,
  } = useEmailSignatureEditorState(true)

  function updatePreviewFromEditor() {
    setPreviewHtml(wrapSignaturePreview(getEditorHtml()))
    setIsDirty(true)
  }

  function applyLoadedHtml(html, saved) {
    setLoadedHtml(html)
    setHasSavedSignature(saved)
    setPreviewHtml(wrapSignaturePreview(html))
    setIsDirty(false)
    if (editorReady) {
      updateEditorContents(html)
    }
  }

  function loadSignature() {
    setIsLoading(true)
    return axios
      .get(CONCIERGE_EMAIL_SIGNATURE_API_URL)
      .then(({ data }) => {
        const initialHtml = buildInitialEditorHtml(
          data.email_signature_text,
          data.email_signature_image
        )
        applyLoadedHtml(initialHtml, savedSignatureHasContent(data))
      })
      .catch(() => applyLoadedHtml("", false))
      .finally(() => setIsLoading(false))
  }

  function handleSignatureImageReady(imageDataUrl) {
    insertImageIntoEditor(imageDataUrl)
    updatePreviewFromEditor()
  }

  function handleSave() {
    const signatureHtml = getEditorHtml()
    const formData = new FormData()
    formData.append("email_signature_text", signatureHtml)
    formData.append("clear_signature_image", "true")
    setIsSaving(true)
    axios
      .patch(CONCIERGE_EMAIL_SIGNATURE_API_URL, formData, MULTIPART_HEADERS)
      .then(() => {
        setHasSavedSignature(editorHasContent(signatureHtml))
        setIsDirty(false)
        onStatusMessage?.("Email signature saved for this account.")
      })
      .finally(() => setIsSaving(false))
  }

  useEffect(() => {
    loadSignature()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!editorReady || loadedHtml === null) {
      return
    }
    updateEditorContents(loadedHtml)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorReady, loadedHtml])

  const accent = theme.palette.colors?.brand500 ?? theme.palette.primary.main

  return (
    <Card variant="outlined" sx={recruitProfileSectionCardSx(theme)}>
      <CardHeader
        avatar={
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(137, 47, 206, 0.1)",
              color: accent,
            }}
          >
            <AutoAwesomeOutlinedIcon />
          </Box>
        }
        title="Email signature"
        subheader="Your signature is stored per account. It is added automatically when you send inbox email replies for this account."
        titleTypographyProps={{ variant: "h6", fontWeight: 700 }}
      />
      <CardContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Signatures are account-specific. Switching accounts in the account
          switcher loads that account&apos;s signature without affecting other
          accounts.
        </Alert>
        {isLoading ? (
          <Skeleton variant="rounded" height={320} />
        ) : (
          <Row
            sx={{
              gap: 2,
              alignItems: "stretch",
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Column sx={{ flex: 1, gap: 2 }}>
              <Box
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                  "& .tox-tinymce": {
                    border: "none !important",
                  },
                }}
              >
                <EmailSignatureEditor
                  editorRef={editorRef}
                  setEditorReady={setEditorReady}
                  onEditorChange={updatePreviewFromEditor}
                />
              </Box>
              <SignatureImageUpload
                disabled={!editorReady}
                onImageReady={handleSignatureImageReady}
              />
              <Divider />
              <Row sx={{ justifyContent: "flex-end", gap: 1 }}>
                <Button
                  color="inherit"
                  disabled={!isDirty || isSaving}
                  onClick={loadSignature}
                >
                  Reset
                </Button>
                <LoadingButton
                  variant="contained"
                  loading={isSaving}
                  disabled={!isDirty}
                  onClick={handleSave}
                >
                  Save signature
                </LoadingButton>
              </Row>
            </Column>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SignaturePreviewPanel
                previewHtml={previewHtml}
                hasSavedSignature={hasSavedSignature}
              />
            </Box>
          </Row>
        )}
      </CardContent>
    </Card>
  )
}
